import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '@/components/ui/use-toast';
import { UserStatus } from '@/components/auth/UserStatus';
import { authService, NO_ROLE_ASSIGNED, SEAT_LIMIT_REACHED, type AuthResponse, type PendingReason } from '@/services/auth-service';
import { AuthSyncService } from '@/services/auth-sync-service';
import { backendUserProfilesService } from '@/services/backend-user-profiles-service';
import { LoginShell, loginVariant } from '@/components/auth/login/LoginShell';
import { useTranslation } from 'react-i18next';
import { logError } from '@/lib/logger';
import { consumePostLoginTarget, rememberDesktopDeepLink } from '@/lib/post-login-redirect';

interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/** Heading and text of the waiting room per reason; no reason = approval pending. */
function pendingText(reason: PendingReason | undefined): { title: string; description: string } {
  if (reason === SEAT_LIMIT_REACHED) {
    return { title: 'auth.seatLimitReached', description: 'auth.seatLimitReachedDescription' };
  }
  if (reason === NO_ROLE_ASSIGNED) {
    return { title: 'auth.noRoleAssigned', description: 'auth.noRoleAssignedDescription' };
  }
  return { title: 'auth.pendingApproval', description: 'auth.pendingApprovalDescription' };
}

export function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingApprovalUser, setPendingApprovalUser] = useState<User | null>(null);
  // Why the person waits, when the backend said so: every seat taken (#1340)
  // or no role yet (#1433). Same waiting room, but they should know why.
  const [pendingReason, setPendingReason] = useState<PendingReason | undefined>(undefined);
  // What their organisation tells people who wait (#1433), plain text.
  const [organisationMessage, setOrganisationMessage] = useState<string | undefined>(undefined);
  // Refused tenant (#530) — a dead end, not a queue. Kept apart from
  // pendingApprovalUser so the screen never offers an approval that cannot come.
  const [accessDeniedEmail, setAccessDeniedEmail] = useState<string | null>(null);
  // Set once the browser is on its way to Microsoft; the initial auth check is isLoading only.
  const [redirecting, setRedirecting] = useState(false);

  // Handle Azure AD redirect response and check existing auth
  useEffect(() => {
    // `/#/w=…` (#1135): the redirect to the desktop below — immediately, or
    // after the Entra round trip — must carry the deep link along.
    rememberDesktopDeepLink(window.location.hash);
    // Refused tenant or waiting person; false when the response lets them in.
    // The message stands below the sign-in card (#1529), no toast on top of it.
    const showRefusal = (response: AuthResponse): boolean => {
      // Refused for a reason no approval can lift (#530): the account's
      // tenant is not on the allowlist. Must be checked BEFORE the
      // isActive branch, which would otherwise promise an approval that
      // is never going to come.
      if (response.accessDenied) {
        setAccessDeniedEmail(response.user.email);
        return true;
      }
      if (response.user.isActive) return false;

      // User is pending approval - show message but don't logout
      setPendingApprovalUser(response.user);
      setPendingReason(response.pendingReason);
      setOrganisationMessage(response.organisationMessage);
      return true;
    };
    const check = async () => {
      try {
        setIsLoading(true);
        const redirectResult = await authService.handleAzureADRedirect();
        if (redirectResult) {
          if (showRefusal(redirectResult)) {
            setIsLoading(false);
            return;
          }

          // Zugang hat, wer den Desktop ODER die shell-freien Routen darf
          // (#504) — letztere hängen an `pdb`, damit ein reiner PDB-Nutzer
          // nicht schon hier abprallt.
          const profile = await backendUserProfilesService.getMyProfile();
          const allowed = profile?.allowed_applications ?? [];
          if (profile && !allowed.includes('webos') && !allowed.includes('pdb')) {
            // User is active but has no access - create access request
            try {
              await backendUserProfilesService.requestWebOSAccess();
            } catch (err) {
              console.error('Failed to create webOS access request:', err);
            }
            setPendingApprovalUser(redirectResult.user);
            setIsLoading(false);
            return;
          }

          try {
            await AuthSyncService.onUserLogin();
          } catch (syncError) {
            console.error('Fehler bei der Settings-Synchronisation nach dem Login:', syncError);
          }
          setCurrentUser(redirectResult.user);
          toast({ title: t('auth.loginSuccess'), description: `${t('auth.welcomeBack')}, ${redirectResult.user.name}!` });
          // Deep-Link-Ziel, falls der User über eine geschützte Route kam;
          // sonst die Startseite, die zu seinen Anwendungen passt (#504).
          navigate(consumePostLoginTarget(allowed), { replace: true });
          return;
        }
        setIsLoading(false);

        // Check if already authenticated (e.g. token restore from localStorage)
        const isAuth = await authService.isAuthenticated();
        if (isAuth) {
          // A token alone does not let anyone in (#1433): without asking the
          // backend, a waiting person was sent to the desktop and bounced
          // straight back, flickering between `/` and `/desktop`.
          const refusal = await authService.checkRestoredSession();
          if (refusal && showRefusal(refusal)) return;

          const user = await authService.getCurrentUser();

          // Zugang prüfen, bevor navigiert wird (#504: webos ODER pdb)
          const existingProfile = await backendUserProfilesService.getMyProfile();
          const existingAllowed = existingProfile?.allowed_applications ?? [];
          if (
            existingProfile &&
            !existingAllowed.includes('webos') &&
            !existingAllowed.includes('pdb')
          ) {
            try {
              await backendUserProfilesService.requestWebOSAccess();
            } catch (err) {
              console.error('Failed to create webOS access request:', err);
            }
            setPendingApprovalUser(user);
            setIsLoading(false);
            return;
          }

          setCurrentUser(user);
          const target = consumePostLoginTarget(existingAllowed);
          setTimeout(() => navigate(target, { replace: true }), 100);
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setCurrentUser(null);
        setIsLoading(false);
      }
    };
    check();
  }, [navigate, toast, t]);

  const handleLogin = async () => {
    setIsLoading(true);
    setRedirecting(true);
    try {
      // Azure AD: Start redirect login (user will be redirected to Microsoft)
      await authService.startAzureADLogin();
      // Note: User will be redirected, so we don't reach this point
    } catch (error) {
      logError(error as Error, { context: 'LoginPage', action: 'handleLogin' });
      toast({
        title: t('auth.loginFailed'),
        description: error instanceof Error ? error.message : t('auth.invalidCredentials'),
        variant: 'destructive',
      });
      setIsLoading(false);
      setRedirecting(false);
    }
  };

  // Whoever waits or was refused is stuck with that Microsoft account: the
  // reload asks the backend again and lands in the same place (#1452). Only
  // dropping the cached account and letting Microsoft ask which one to use
  // gets them out.
  const handleSwitchAccount = async () => {
    setIsLoading(true);
    setRedirecting(true);
    try {
      await authService.clearForRelogin();
      await authService.startAzureADLogin();
    } catch (error) {
      logError(error as Error, { context: 'LoginPage', action: 'handleSwitchAccount' });
      toast({ title: t('auth.loginFailed'), description: t('auth.errorOccurred'), variant: 'destructive' });
      setIsLoading(false);
      setRedirecting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setCurrentUser(null);
      toast({ title: t('auth.logoutSuccess'), description: t('auth.logoutMessage') });
    } catch (error) {
      toast({ title: t('auth.errorOccurred'), description: t('auth.errorOccurred'), variant: 'destructive' });
    }
  };

  const blockedEmail = accessDeniedEmail ?? pendingApprovalUser?.email;
  const siemens = loginVariant() === 'siemens';
  const pending = pendingText(pendingReason);

  const card = currentUser ? (
    <UserStatus currentUser={currentUser} onLogout={handleLogout} />
  ) : (
    <>
      {/* Waiting or refused (#1452): the Microsoft button cannot help them, only another account can. */}
      {blockedEmail ? (
        <button
          type="button"
          className="login-btn login-btn-secondary"
          onClick={handleSwitchAccount}
          disabled={isLoading}
          data-testid="login-switch-account"
        >
          {redirecting ? <span className="login-spinner" aria-hidden="true" /> : null}
          {t('auth.switchAccount')}
        </button>
      ) : (
        <button
          type="button"
          className="login-btn login-btn-primary"
          onClick={handleLogin}
          disabled={isLoading}
          data-testid="login-sign-in"
        >
          {redirecting ? <span className="login-spinner" aria-hidden="true" /> : !siemens && <MicrosoftLogo />}
          {redirecting
            ? t('auth.loginPage.redirecting')
            : t(siemens ? 'auth.loginPage.siemens.signIn' : 'auth.loginPage.signIn')}
        </button>
      )}
      <p className="login-hint" data-testid="login-hint">
        {blockedEmail
          ? t('auth.tenantNotAllowedAccount', { email: blockedEmail })
          : t(siemens ? 'auth.loginPage.siemens.hint' : 'auth.loginPage.hint')}
      </p>
    </>
  );

  // Tenant refused (#530): a statement, not a waiting room, and no access
  // request, because there is no one on the other side to approve it.
  const status = accessDeniedEmail ? (
    <div className="login-status login-status-deny" role="alert" data-testid="login-status-denied">
      <BlockedIcon />
      <h3>{t('auth.tenantNotAllowed')}</h3>
      <p>{t('auth.tenantNotAllowedDescription')}</p>
    </div>
  ) : pendingApprovalUser ? (
    <div className="login-status login-status-warn" data-testid="login-status-pending">
      <ClockIcon />
      <h3>{t(pending.title)}</h3>
      <p>{t(pending.description, { email: pendingApprovalUser.email })}</p>
      {organisationMessage && (
        <div className="login-status-org" data-testid="login-organisation-message">
          <b>{t('auth.organisationMessage')}</b>
          {/* Plain text from the organisation's settings; React escapes it. */}
          <p>{organisationMessage}</p>
        </div>
      )}
      <p className="login-status-note">{t('auth.pendingApprovalNotification')}</p>
    </div>
  ) : null;

  return <LoginShell card={card} status={status} />;
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function BlockedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </svg>
  );
}
